import { useState } from "react";
import { useAtomValue } from "jotai";

import {
  Button,
  EmptyState,
  Icon,
  PageHeader,
  Panel,
  StatusMark,
} from "@go-novus/ui";

import {
  forgetPlaygroundUid,
  playgroundSessionAtom,
  readPlaygroundFailureMessage,
} from "#/features/playground";
import { readSettingsFailureMessage, settingsAtom } from "#/features/settings";
import * as m from "#/paraglide/messages";
import { useFailureNotice } from "#/shared/ui/failure-notice";

import { PlaygroundWidget } from "./playground-widget";

/**
 * 后台里的试聊：面板里只有店面那只挂件，标题栏只留 New test——挂件自带头部，
 * 页面再摆一条就成了两层头。
 *
 * 挂件读的是已保存的配置，不是 Settings 里的草稿：这里试的是店面此刻真正在
 * 跑的那一份（GON-7）。
 */
export function PlaygroundPage() {
  const [resetting, setResetting] = useState(false);
  const [invalidatedUid, setInvalidatedUid] = useState<string>();
  const session = useAtomValue(playgroundSessionAtom);
  const settings = useAtomValue(settingsAtom);
  const saved = settings.data;
  // 挂件要已保存的配置才挂得起来，所以读配置失败和开会话失败一样是死路。
  const failed = session.isError ? session : settings.isError ? settings : null;
  const blockingFailure = useFailureNotice({
    failedAt: failed?.errorUpdatedAt ?? 0,
    hasContent: false,
    message:
      failed === null
        ? null
        : (readPlaygroundFailureMessage(failed.error) ??
          readSettingsFailureMessage(failed.error) ??
          m.playground_failure_unexpected()),
  });

  return (
    <div className="flex h-full min-h-0 flex-col gap-4.5">
      <PageHeader
        actions={
          <>
            <StatusMark tone="ready">{m.playground_live_config()}</StatusMark>
            <Button
              disabled={session.isFetching}
              onClick={() => {
                // 立即卸载旧测试，查询通知迟于 refetch 完成时也不能重新挂回。
                setInvalidatedUid(session.data?.uid);
                setResetting(true);
                forgetPlaygroundUid(session.data?.uid);
                void session.refetch().finally(() => {
                  setResetting(false);
                });
              }}
              size="sm"
            >
              {m.playground_reset()}
            </Button>
          </>
        }

        title={m.playground_page_title()}
      />
      <p className="text-label text-[var(--content-fg-subtle)]">
        {m.playground_real_business_notice()}
      </p>
      <Panel className="flex min-h-0 flex-1 flex-col">
        {blockingFailure !== null ? (
          <EmptyState heading={blockingFailure} icon={<Icon name="info" />} />
        ) : (
          !resetting &&
          session.data !== undefined &&
          session.data.uid !== invalidatedUid &&
          saved !== undefined && (
            <PlaygroundWidget
              onSessionReplaced={() => {
                setInvalidatedUid(session.data.uid);
                void session.refetch();
              }}
              playgroundSession={session.data}
              settings={saved}
            />
          )
        )}
      </Panel>
    </div>
  );
}
